import {
  COUNTRIES_WITHOUT_DEBIT_CARD_SURCHARGING,
  isCaProvinceAllowingSurcharge,
  SURCHARGING_ENABLED_COUNTRIES,
  US_STATES_FOR_POST_DEPOSIT_DEFAULT_ACH_PAYMENT,
} from 'constants/cashFlow'
import { orgSelectors } from 'ducks/orgs'
import { useNotify, useTranslate } from 'ra-core'
import { getStripeCustomerSurchargeSettings } from 'pages/cashFlow/utils'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { CashFlowConfiguration, ConvertToCashFlowSettings, PaymentMethodRecordType } from 'types/cashFlow'

const restClientInstance = restClient(window.API_ROOT + '/api')

export const useGetCashFlowConfigurations = (
  orgId: number | undefined,
  page: number,
  limit: number = 10,
  isUpdated?: boolean,
  financePartnerId?: number
) => {
  const [results, setResults] = useState<CashFlowConfiguration[]>([])
  useEffect(() => {
    if (orgId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/payments/configuration/`,
      })
        .then((res) => {
          setResults(res.data)
        })
        .catch((err) => {
          console.log('err', err)
        })
    } else if (financePartnerId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `finance_partners/${financePartnerId}/payments/configuration/`,
      })
        .then((res) => {
          setResults(res.data)
        })
        .catch((err) => {
          console.log('err', err)
        })
    }
  }, [orgId, page, limit, isUpdated])

  return results
}

export const getIsSurchargingAllowed = (countryIso2: string | undefined) => {
  return Boolean(countryIso2 && SURCHARGING_ENABLED_COUNTRIES.includes(countryIso2))
}

export const isStripeCustomerSurchargeConfigured = (settings: {
  customer_surcharge_percent: string | null
  customer_surcharge_fixed_minor_units: number | null
}) =>
  settings.customer_surcharge_percent != null || settings.customer_surcharge_fixed_minor_units != null

export const useStripeCustomerSurchargeConfigured = (shouldFetch: boolean = true) => {
  const orgId = useSelector(orgSelectors.getOrg)?.id
  const isStripePsp = useSelector(orgSelectors.getIsCashflowStripePsp)
  const [isConfigured, setIsConfigured] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!orgId || !isStripePsp || !shouldFetch) {
      setIsConfigured(true)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    getStripeCustomerSurchargeSettings(orgId)
      .then((data) => {
        if (!cancelled) {
          setIsConfigured(isStripeCustomerSurchargeConfigured(data))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsConfigured(true)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [orgId, isStripePsp, shouldFetch])

  return { isConfigured, isLoading }
}

type PaymentMethodChoiceOptions = {
  /** When true, only methods offered at checkout (``is_active``) — use for payment requests, not milestone config. */
  checkoutReadyOnly?: boolean
}

export const useGetPaymentMethodChoices = (options: PaymentMethodChoiceOptions = {}) => {
  const { checkoutReadyOnly = false } = options
  const [countryChoices, setCountryChoices] = useState<{ id: string; name: string; max_payment_amount: number }[]>([])

  const orgId = useSelector(orgSelectors.getOrg)?.id
  const orgCountry = useSelector(orgSelectors.getOrgIso2)
  // Effective PSP for the org: explicit ``preferred_cashflow_psp_integration`` (set by the PSP switch endpoint or
  // first PSP approval) falls back to the org's resolved ``cashflow_psp_integration``. Used to scope the dropdown
  // to one PSP's methods because every payment request / milestone is routed through a single PSP - mixing methods
  // from both PSPs in the picker would let the user create payment requests against the wrong (or an empty) PSP.
  const preferredPspFromConfig = useSelector(orgSelectors.getCashflowPreferredPspIntegration)
  const cashflowPspIntegration = useSelector(orgSelectors.getCashFlowPspIntegration)
  const preferredPsp = preferredPspFromConfig || cashflowPspIntegration
  const translate = useTranslate()
  const notify = useNotify()

  useEffect(() => {
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/payment_methods/`,
    })
      .then((res) => {
        setCountryChoices(
          res.data
            ?.filter((pmtMethod) =>
              checkoutReadyOnly ? pmtMethod.is_active : pmtMethod.is_available_for_configurations
            )
            // Keep ``not_integrated`` methods (e.g. Bank Transfer) plus methods belonging to the org's currently
            // preferred PSP. When no PSP is preferred yet, show everything (e.g. supplier-only orgs that don't
            // surface a PSP integration but still need to pick a payment method).
            ?.filter((pmtMethod) => {
              if (!preferredPsp) return true
              if (pmtMethod.psp_integration === 'not_integrated') return true
              return pmtMethod.psp_integration === preferredPsp
            })
            ?.map((pmtMethod) => ({
              id: pmtMethod.payment_method_type,
              name: getTranslatedPaymentMethodLabel(translate, pmtMethod.payment_method_type),
              max_payment_amount: pmtMethod.max_payment_amount,
            }))
        )
      })
      .catch((err) => {
        notify(translate('Unable to fetch available payment methods'), 'warning')
      })
  }, [orgCountry, preferredPsp, checkoutReadyOnly])

  return countryChoices
}

/** Defaults from the org auto-apply milestone (``GET .../payment_methods/default_accepted/``). */
export const useDefaultAcceptedPaymentMethods = () => {
  const [defaultAcceptedPaymentMethods, setDefaultAcceptedPaymentMethods] = useState<string[]>([])
  const orgId = useSelector(orgSelectors.getOrg)?.id
  const orgCountry = useSelector(orgSelectors.getOrgIso2)

  useEffect(() => {
    if (!orgId) {
      setDefaultAcceptedPaymentMethods([])
      return
    }
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/payment_methods/default_accepted/`,
    })
      .then((res) => {
        setDefaultAcceptedPaymentMethods(res.data?.default_accepted_payment_methods ?? [])
      })
      .catch(() => {
        setDefaultAcceptedPaymentMethods([])
      })
  }, [orgId, orgCountry])

  return defaultAcceptedPaymentMethods
}

/** Pre-selected methods for ad-hoc payment requests: milestone defaults ∩ checkout-ready picker options. */
export const resolvePaymentRequestDefaultMethods = (
  paymentMethodChoices: { id: string }[] | undefined,
  configDefaults: string[]
): string[] => {
  const choiceIds = new Set(paymentMethodChoices?.map((choice) => choice.id) ?? [])
  return configDefaults.filter((method) => choiceIds.has(method))
}

export const getHasPaymentCap = (paymentMethods: any, orgCountry: string | undefined) => {
  let capAmount = 6000
  if (orgCountry === 'AU') {
    capAmount = 10000
  }

  return paymentMethods?.every(
    (choice) => choice.id === 'ach' || choice.id === 'becs_direct_debit' || choice.max_payment_amount === capAmount
  )
}

export const sanitizeCashFlowConfigForm = (vals) => {
  if (vals.installer_incentive_milestone_display === '' || vals.installer_incentive_milestone_display == null) {
    vals.installer_incentive_milestone_display = 'prorate'
  }
  vals.payment_milestone_configurations?.forEach((milestone, i) => {
    milestone.enable_payment_cta_on_proposal = !!milestone.enable_payment_cta_on_proposal

    // make sure the last milestone has set_value_to_remaining_amount_due=true but no others do
    // and if this is a deposit_only config set_value_to_remaining_amount_due must be false
    if (i + 1 < vals.payment_milestone_configurations.length || vals.deposit_only) {
      vals.payment_milestone_configurations[i].set_value_to_remaining_amount_due = false
    } else {
      vals.payment_milestone_configurations[i].set_value_to_remaining_amount_due = true
      vals.payment_milestone_configurations[i].percentage = null
      vals.payment_milestone_configurations[i].min_fixed = null
      vals.payment_milestone_configurations[i].max_fixed = null
    }

    if (milestone.remove_from_percentage_calcs) {
      if (i === 0) {
        // if the first milestone has remove_from_percentage_calcs set to true, remove percentage, min and max
        milestone.percentage = null
        milestone.min_fixed = null
        milestone.max_fixed = null
      } else {
        // if this is not the first milestone then remove_from_percentage_cals should be false and fixed_amount should be null
        // those fields are only allowed on the first milestone
        milestone.remove_from_percentage_cals = false
        milestone.fixed_amount = null
      }
    } else {
      milestone.fixed_amount = null
    }

    // don't allow any milestone other than the first to be marked as a deposit
    if (i > 0 && milestone.is_deposit) {
      milestone.is_deposit = false
    }

    // add payment_number to each milestone before submitting to make sure things like re-sorting or deleting milestones doesn't end up
    // leading to incorrect payment numbers being submitted
    vals.payment_milestone_configurations[i].payment_number = i + 1
  })
  return vals
}

export const showCreditCardTooltipCap = (paymentMethods: any, hasPaymentCap: boolean) => {
  if (!paymentMethods || !hasPaymentCap) return false
  if (
    paymentMethods.includes('credit_card_standard') ||
    paymentMethods.includes('credit_card_premium') ||
    paymentMethods.includes('credit_card_international') ||
    paymentMethods.includes('debit_card_standard') ||
    paymentMethods.includes('debit_card_premium') ||
    paymentMethods.includes('debit_card_international') ||
    paymentMethods.includes('apple_google_pay')
  )
    return true
}

export const showAppleGooglePaySurchargeWarning = (surchargingEnabled: boolean, paymentMethods: any) => {
  if (!surchargingEnabled) return false
  return paymentMethods.includes('apple_google_pay')
}

export const showDebitCardSurchargeWarning = (
  surchargingEnabled: boolean,
  paymentMethods: any,
  orgCountry: string | undefined
) => {
  if (!orgCountry) return false
  if (!surchargingEnabled || !COUNTRIES_WITHOUT_DEBIT_CARD_SURCHARGING.includes(orgCountry)) return false
  if (
    paymentMethods.includes('debit_card_standard') ||
    paymentMethods.includes('debit_card_premium') ||
    paymentMethods.includes('debit_card_international')
  )
    return true
}

export const getTranslatedPaymentMethodLabel = (translateFn: Function, pmt_type: string) => {
  switch (pmt_type) {
    case 'local_bank_transfer':
      return translateFn('Local Bank Transfer')
    case 'credit_card_standard':
      return translateFn('Credit Card - Standard')
    case 'credit_card_premium':
      return translateFn('Credit Card - Premium')
    case 'credit_card_international':
      return translateFn('Credit Card - International')
    case 'debit_card_standard':
      return translateFn('Debit Card - Standard')
    case 'debit_card_premium':
      return translateFn('Debit Card - Premium')
    case 'debit_card_international':
      return translateFn('Debit Card - International')
    case 'ach':
      return 'ACH'
    case 'apple_google_pay':
      return translateFn('Apple/Google Pay')
    case 'becs_direct_debit':
      return translateFn('Direct Debit')
    case 'sepa_credit':
      return translateFn('Open Banking')
    case 'direct_payment':
      return translateFn('Bank Transfer')
    case 'stripe_card':
      return translateFn('Card')
    case 'stripe_apple_pay':
      return translateFn('Apple Pay')
    case 'stripe_google_pay':
      return translateFn('Google Pay')
    case 'stripe_us_bank_account':
      return translateFn('ACH')
    case 'stripe_afterpay_clearpay':
      return translateFn('Afterpay / Clearpay')
    case 'stripe_payto':
      return translateFn('PayTo Bank Transfer')
    case 'stripe_klarna':
      return translateFn('Klarna')
    case 'stripe_pay_by_bank':
      return translateFn('Pay by Bank')
    case 'stripe_acss_debit':
      return translateFn('Pre-authorized debit (ACSS)')
    case 'stripe_nz_bank_account':
      return translateFn('Pay by Bank')
    case 'stripe_paynow':
      return translateFn('PayNow')
    case 'stripe_paypal':
      return translateFn('PayPal')
    case 'stripe_au_becs_debit':
      return translateFn('AU BECS Direct Debit')
    case 'stripe_affirm':
      return translateFn('Affirm')
    case 'stripe_us_bank_account_ach_payments':
      return translateFn('ACH')
    case 'stripe_bacs_debit':
      return translateFn('Bacs Direct Debit')
    default:
      return translateFn('Offline')
  }
}

export const useGetPaymentMethods = (is_active: boolean = true) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRecordType[]>([])

  const orgId = useSelector(orgSelectors.getOrg)?.id
  const orgCountry = useSelector(orgSelectors.getOrgIso2)
  const translate = useTranslate()
  const notify = useNotify()

  useEffect(() => {
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/payment_methods/`,
      params: {
        is_active: is_active,
      },
    })
      .then((res) => {
        setPaymentMethods(res.data)
      })
      .catch((err) => {
        notify(translate('Unable to fetch available payment methods'), 'warning')
      })
  }, [orgCountry])

  return paymentMethods
}

/** PSP from approved CashFlow onboarding; use with `getDefaultPaymentMethodsForCountry` (milestone bootstrap only). */
export const useCashflowPspForDefaultPaymentMethods = (): string | undefined => {
  const blueSnapApproved = useSelector(orgSelectors.getBlueSnapOnboardingIsApproved)
  const stripeApproved = useSelector(orgSelectors.getStripeOnboardingIsApproved)
  const preferredPsp = useSelector(orgSelectors.getCashflowPreferredPspIntegration)
  const psp = useSelector(orgSelectors.getCashFlowPspIntegration)
  const effectivePsp = preferredPsp || psp
  if (stripeApproved && effectivePsp === 'stripe') {
    return 'stripe'
  }
  if (blueSnapApproved) {
    return effectivePsp ?? undefined
  }
  return undefined
}

export const getDefaultPaymentMethodsForCountry = (
  countryIso2: string,
  isDeposit: boolean,
  businessState: string,
  psp?: string
) => {
  if (psp === 'stripe') {
    return ['stripe_card']
  }
  if (countryIso2 === 'GB') {
    if (isDeposit)
      return [
        'credit_card_standard',
        'credit_card_premium',
        'credit_card_international',
        'local_bank_transfer',
        'apple_google_pay',
      ]
    else return ['local_bank_transfer']
  } else if (countryIso2 === 'AU') {
    return [
      'credit_card_standard',
      'credit_card_premium',
      'credit_card_international',
      'becs_direct_debit',
      'apple_google_pay',
    ]
  } else if (countryIso2 === 'US') {
    if (isDeposit || !US_STATES_FOR_POST_DEPOSIT_DEFAULT_ACH_PAYMENT.includes(businessState)) {
      return ['credit_card_standard', 'credit_card_premium', 'credit_card_international', 'ach', 'apple_google_pay']
    } else {
      // Ref: https://github.com/open-solar/opensolar-todo/issues/11707
      // Default to ACH if their business is in state in the US where pros cannot surcharge
      return ['ach']
    }
  }
  return []
}
export const useGetConvertToCashFlowSetting = (orgId: number | undefined) => {
  const [settings, setSettings] = useState<ConvertToCashFlowSettings | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(true)

  const fetchSettings = () => {
    setIsFetching(true)
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/payments/convert_to_cashflow/settings/`,
    })
      .then((res) => {
        setSettings(res.data)
      })
      .catch((err) => {
        setSettings(null)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  useEffect(() => {
    fetchSettings()
  }, [orgId])

  return { settings, isFetching }
}

export const validateMilestones = (vals: any) => {
  const milestones = vals.payment_milestone_configurations
  let milestoneNum = 0

  const isValid = !milestones.some((milestone, index) => {
    if (!milestone.title.trim()) {
      milestoneNum = index + 1
      return true
    }
    return false
  })

  return { isValid, milestoneNum }
}
